import { Component, EventEmitter, Input, Output } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { E2eSuite, E2eTestsService } from '../../e2e-tests/services/e2e-tests.service';

/**
 * Captures ONE assessment result as an anonymized E2E replay fixture.
 *
 * The operator picks the assessment; the patient behind it is derived
 * server-side from the ProResult, so the fixture always holds exactly the
 * answers that were on screen. All PII (name/email/phone/token) is stripped
 * before anything is stored.
 */
@Component({
  standalone: false,
  selector: 'app-capture-fixture-button',
  templateUrl: './capture-fixture-button.component.html',
  styleUrls: ['./capture-fixture-button.component.scss'],
})
export class CaptureFixtureButtonComponent {
  /** ProResult _id of the assessment to capture. */
  @Input() proResultId: string;
  /** `xs` matches the patient record's inline assessment actions. */
  @Input() size: 'sm' | 'xs' = 'sm';
  /** Menu opens upwards when the button sits near the bottom of a panel. */
  @Input() dropup = false;
  /** Hide the text label and keep only the icon (tight grids). */
  @Input() iconOnly = false;
  @Output() captured = new EventEmitter<string>();

  readonly suites: { id: E2eSuite; name: string }[] = [
    { id: 'gateway', name: 'Gateway suite (PRO player)' },
    { id: 'doctor', name: 'Doctor suite (doctor app)' },
    { id: 'quiz', name: 'Quiz suite (long funnel)' },
  ];

  capturing = false;

  constructor(private readonly e2eTestsService: E2eTestsService) {}

  async capture(suite: E2eSuite = 'gateway', event?: Event) {
    // The row/card behind this button toggles expand on click.
    event?.stopPropagation();
    if (!this.proResultId || this.capturing) return;

    this.capturing = true;
    try {
      const report = await firstValueFrom(
        this.e2eTestsService.captureFixtures([this.proResultId], suite),
      );
      if (report.errors.length) {
        alert(`Could not capture this assessment:\n${report.errors[0].reason}`);
        return;
      }
      const [created] = report.created;
      alert(
        'E2E fixture created from this assessment.' +
          `\n\nLabel: ${created?.label}\nCategory: ${created?.category}\nSuite: ${suite}` +
          '\n\nManage it on the E2E Tests page.',
      );
      this.captured.emit(created?.fixtureId);
    } catch (err: any) {
      alert(err?.error?.message || 'Failed to capture E2E fixture');
    } finally {
      this.capturing = false;
    }
  }
}
